/**
 * FullVSL overlay runtime renderer (FUL-11).
 *
 * Renders the six MVP overlay moments (FUL-10 templates) into the player frame
 * against the safe-zone layout (FUL-8): start, autoplay, minihook, pause,
 * cta_reveal, end. Each overlay is positioned inside its configured safe-zone
 * slot and revealed/hid by its `timing.show` moment.
 *
 * Split into:
 *  - Pure evaluation helpers (unit-tested, no DOM): asset-type inference, the
 *    html-asset security disable, mixed-content detection, field validation
 *    (shared with the editor), pause min-watch threshold, timestamp arrival,
 *    visibility match, and the show-moment decision.
 *  - `OverlayController`: owns the overlay DOM layer, renders panels per
 *    enabled overlay, positions them via the safe-zone regions, drives
 *    reveal/hide from player events, renders assets safely (img/video only,
 *    never innerHTML), and fires `pause_overlay_shown` / `pause_overlay_action`
 *    through the player event sink.
 *
 * Security (PRD §9.7, US-5.3): text is bound via textContent (never innerHTML);
 * `assetType: "html"` is disabled (text-only fallback); assets render only as
 * typed `<img>`/`<video>` elements; broken/mixed-content assets fall back to
 * text-only with no broken-HTML.
 *
 * Contract: @fullvsl/contracts Overlay/OverlayContent/OverlayTiming/OverlayAction
 * (FUL-3 §3). Templates: @fullvsl/contracts overlay-templates (FUL-10).
 */

import type {
  Overlay,
  OverlayAction,
  OverlayContent,
  OverlayType,
  OverlayVisibility,
  PlayerOverlayEvent,
} from "../../contracts/src/contracts";
import { PAUSE_OVERLAY_MIN_WATCH_SEC } from "../../contracts/src/overlay-templates";
import {
  OVERLAY_POSITIONS,
  type OverlayPosition,
  type SafeZoneLayout,
  type SafeZoneRegion,
} from "./fullvsl-safe-zones";

/* =====================================================================================
 * Pure helpers (no DOM — unit-tested)
 * ===================================================================================== */

/** Image asset URL extensions (rendered via <img>). */
const IMAGE_EXT = ["png", "jpg", "jpeg", "webp", "gif", "svg", "avif"];
/** Video asset URL extensions (rendered via <video>). */
const VIDEO_EXT = ["mp4", "webm", "ogv", "mov", "m4v"];

/** Inferred asset kind for a custom asset URL. */
export type InferredAssetKind = "image" | "video";

/**
 * Infer the asset kind from a URL extension. Unknown/missing extensions return
 * null (caller must require an explicit assetType). Query/hash fragments are
 * stripped before matching.
 */
export function inferAssetKind(url: string): InferredAssetKind | null {
  if (!url) return null;
  try {
    const u = new URL(url, "https://placeholder.invalid");
    const path = u.pathname.toLowerCase();
    const dot = path.lastIndexOf(".");
    if (dot < 0) return null;
    const ext = path.slice(dot + 1);
    if (IMAGE_EXT.includes(ext)) return "image";
    if (VIDEO_EXT.includes(ext)) return "video";
    return null;
  } catch {
    return null;
  }
}

/**
 * Resolve the effective asset kind for an overlay content. `html` is disabled in
 * the MVP (security) → returns `disabled: true` so the renderer falls back to
 * text-only. When `assetType` is unset, it is inferred from the URL; ambiguous
 * URLs disable the asset.
 */
export function resolveAssetKind(
  content: Pick<OverlayContent, "assetUrl" | "assetType">,
): { kind: InferredAssetKind | null; disabled: boolean } {
  if (content.assetType === "html") return { kind: null, disabled: true };
  if (!content.assetUrl) return { kind: null, disabled: false };
  if (content.assetType === "image" || content.assetType === "video") {
    return { kind: content.assetType, disabled: false };
  }
  const inferred = inferAssetKind(content.assetUrl);
  return { kind: inferred, disabled: false };
}

/**
 * Mixed-content check: an `http://` asset on an `https://` page will be blocked
 * by the browser, so the renderer omits it (text-only fallback). Pure variant
 * takes the page protocol; the controller reads `location.protocol`.
 */
export function isMixedContent(assetUrl: string, pageProtocol: string): boolean {
  if (!assetUrl) return false;
  if (!/^https:/i.test(pageProtocol)) return false;
  return /^http:/i.test(assetUrl);
}

/** Whether an asset URL parses as a valid http(s) URL. */
export function isValidAssetUrl(url: string): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/** Minimum watched seconds before the pause overlay may appear (FUL-10 §4.4). */
export function pauseThresholdMet(maxWatchedSec: number, minSec = PAUSE_OVERLAY_MIN_WATCH_SEC): boolean {
  return maxWatchedSec >= minSec;
}

/** Whether a timestamp-triggered overlay's moment has arrived. */
export function timestampArrived(atSec: number | undefined, currentTimeSec: number): boolean {
  return atSec != null && atSec >= 0 && currentTimeSec >= atSec;
}

/**
 * Coarse device bucket from a viewport width. Matches the CTA/editor conventions
 * (≤ 640px is mobile). Used by the `visibility` field.
 */
export function deviceFromWidth(widthPx: number): "desktop" | "mobile" {
  return widthPx <= 640 ? "mobile" : "desktop";
}

/** Whether an overlay's visibility targets the current device. */
export function visibilityMatches(
  visibility: OverlayVisibility | undefined,
  device: "desktop" | "mobile",
): boolean {
  if (!visibility || visibility === "all") return true;
  return visibility === device;
}

/** Human labels for each overlay action (used by renderer + editor). */
export const ACTION_LABELS: Record<OverlayAction, string> = {
  continue: "Continuar",
  restart: "Reiniciar do início",
  jump_to_offer: "Ir para a oferta",
  open_cta: "Abrir CTA",
  close: "Fechar",
};

/**
 * The set of actions each overlay type exposes by default (FUL-10 §4). The
 * editor offers these as toggleable action chips per moment.
 */
export const ACTIONS_BY_TYPE: Record<OverlayType, OverlayAction[]> = {
  start: ["continue"],
  autoplay: ["continue"],
  minihook: ["close"],
  pause: ["continue", "restart", "jump_to_offer", "open_cta", "close"],
  cta_reveal: ["open_cta", "close"],
  end: ["open_cta", "restart", "close"],
};

/** Field-length limits enforced on editable text (FUL-10 §5). */
export const FIELD_LIMITS = {
  text: 120,
  subtext: 160,
  buttonLabel: 40,
} as const;

/** Validation error keyed by field. */
export type OverlayErrors = Partial<
  Record<"text" | "subtext" | "buttonLabel" | "bgOpacity" | "position" | "animation" | "at" | "durationMs", string[]>
>;

/**
 * Validate an overlay's editable fields (FUL-10 §5 + §8 AC-2). Shared by the
 * editor live-validation and the renderer (renderer tolerates invalid fields by
 * clamping, but surfaces them). Returns a map of field → error messages.
 */
export function validateOverlay(overlay: Overlay): OverlayErrors {
  const errors: OverlayErrors = {};
  const c = overlay.content;
  const push = (k: keyof OverlayErrors, msg: string): void => {
    (errors[k] ??= []).push(msg);
  };
  if (c.text != null && c.text.length > FIELD_LIMITS.text) {
    push("text", `text must be ≤ ${FIELD_LIMITS.text} chars`);
  }
  if (c.subtext != null && c.subtext.length > FIELD_LIMITS.subtext) {
    push("subtext", `subtext must be ≤ ${FIELD_LIMITS.subtext} chars`);
  }
  if (c.buttonLabel != null && c.buttonLabel.length > FIELD_LIMITS.buttonLabel) {
    push("buttonLabel", `buttonLabel must be ≤ ${FIELD_LIMITS.buttonLabel} chars`);
  }
  if (c.bgOpacity != null && (c.bgOpacity < 0 || c.bgOpacity > 1 || !Number.isFinite(c.bgOpacity))) {
    push("bgOpacity", "bgOpacity must be between 0 and 1");
  }
  if (c.position != null && !(OVERLAY_POSITIONS as readonly string[]).includes(c.position)) {
    push("position", "position must be one of the 7 safe-zone slots");
  }
  if (c.animation != null && !["none", "fade", "slide", "zoom"].includes(c.animation)) {
    push("animation", "animation must be none | fade | slide | zoom");
  }
  if (overlay.timing.show === "timestamp") {
    if (overlay.timing.at == null || overlay.timing.at < 0) {
      push("at", "timestamp overlays require timing.at ≥ 0");
    }
  }
  if (overlay.timing.show === "duration") {
    if (overlay.timing.durationMs == null || overlay.timing.durationMs <= 0) {
      push("durationMs", "duration overlays require timing.durationMs > 0");
    }
  }
  return errors;
}

/** True when validation produced no errors. */
export function isValidOverlay(overlay: Overlay): boolean {
  return Object.keys(validateOverlay(overlay)).length === 0;
}

/** Clamp bgOpacity into [0,1]; defaults to 0.55 when unset/invalid. */
export function clampBgOpacity(v: number | undefined): number {
  if (v == null || !Number.isFinite(v)) return 0.55;
  return Math.min(1, Math.max(0, v));
}

/** Resolve the safe-zone region for an overlay position, defaulting to center. */
export function regionForPosition(
  layout: SafeZoneLayout,
  position: OverlayContent["position"],
): SafeZoneRegion {
  const pos = (position && (OVERLAY_POSITIONS as readonly string[]).includes(position)
    ? position
    : "center") as OverlayPosition;
  return layout.regions[pos];
}

/* =====================================================================================
 * OverlayController (DOM)
 * ===================================================================================== */

/** Watched-progress snapshot fed by the player (mirrors fullvsl-cta). */
export interface OverlayProgress {
  currentTimeSec: number;
  maxWatchedSec: number;
  durationSec: number;
}

/** Lifecycle signals the controller needs from the player. */
export interface OverlayLifecycleState {
  /** True once the first play() has happened. */
  hasStarted: boolean;
  /** True while the video is paused (not ended). */
  isPaused: boolean;
  /** True once the video has ended (drives the end overlay). */
  completed: boolean;
  /** True while the video is muted (drives the autoplay "tap for sound" overlay). */
  isMuted: boolean;
}

/** Callback the controller invokes to resolve an action's effect on playback. */
export interface OverlayActionHandlers {
  /** Resume playback (and unmute when requested, e.g. autoplay/start overlays). */
  continue?: () => void;
  /** Restart the video from the beginning. */
  restart?: () => void;
  /** Jump to the configured offer / CTA destination. */
  jumpToOffer?: () => void;
  /** Open / surface the CTA (delegates to the CTA controller). */
  openCta?: () => void;
}

export interface OverlayControllerOptions {
  /** Mount layer inside the player frame (above media, below controls). */
  layer: HTMLElement;
  /** Overlay configs to render. */
  overlays: Overlay[];
  /** CTA reveal config so cta_reveal mirrors the CTA threshold against live duration. */
  ctaReveal?: { revealMode: "timestamp" | "percent"; revealAt: number; persistAfterReveal?: boolean };
  /** Resolve the current safe-zone layout (re-evaluated on relayout). */
  getLayout: () => SafeZoneLayout;
  /** Emit a pause-overlay player event (player owns seq/identity). */
  emit: (event: PlayerOverlayEvent) => void;
  /** Action side-effects wired by the player. */
  actions?: OverlayActionHandlers;
}

const OVERLAY_CSS = `
.fvsl-ovl{position:absolute;z-index:4;pointer-events:none;display:flex;opacity:0;transition:opacity .25s ease,transform .25s ease;max-width:96%}
.fvsl-ovl.is-visible{opacity:1;pointer-events:auto}
.fvsl-ovl[data-anim="fade"]{transition:opacity .25s ease}
.fvsl-ovl[data-anim="slide"]{transform:translateY(12px)}
.fvsl-ovl[data-anim="slide"].is-visible{transform:translateY(0)}
.fvsl-ovl[data-anim="zoom"]{transform:scale(.94)}
.fvsl-ovl[data-anim="zoom"].is-visible{transform:scale(1)}
.fvsl-ovl-card{background:rgba(8,12,16,.82);border:1px solid rgba(107,216,203,.3);border-radius:8px;padding:14px 16px;color:#fff;font:14px/1.4 "Inter",system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;text-align:center;box-shadow:0 10px 26px rgba(0,0,0,.4);backdrop-filter:blur(6px);display:flex;flex-direction:column;align-items:center;gap:8px;max-width:320px}
.fvsl-ovl-text{font-weight:700;font-size:16px;margin:0}
.fvsl-ovl-sub{margin:0;opacity:.85;font-size:13px}
.fvsl-ovl-asset{max-width:240px;max-height:160px;border-radius:6px;display:block}
.fvsl-ovl-actions{display:flex;flex-wrap:wrap;gap:8px;justify-content:center;margin-top:2px}
.fvsl-ovl-btn{appearance:none;border:0;border-radius:4px;background:#00685f;color:#fff;font:600 14px/1 "Inter",system-ui,-apple-system,sans-serif;padding:10px 16px;cursor:pointer;transition:background .12s ease,transform .12s ease;text-decoration:none;display:inline-block}
.fvsl-ovl-btn:hover{background:#008378}
.fvsl-ovl-btn:active{transform:translateY(1px)}
.fvsl-ovl-btn[data-action="close"]{background:rgba(255,255,255,.16)}
.fvsl-ovl-btn[data-action="close"]:hover{background:rgba(255,255,255,.26)}
.fvsl-ovl--topleft{justify-content:flex-start;align-items:flex-start}
.fvsl-ovl--topright,.fvsl-ovl--top_right{justify-content:flex-end;align-items:flex-start}
.fvsl-ovl--bottomleft,.fvsl-ovl--bottom_left{justify-content:flex-start;align-items:flex-end}
.fvsl-ovl--bottomright,.fvsl-ovl--bottom_right{justify-content:flex-end;align-items:flex-end}
.fvsl-ovl--center{justify-content:center;align-items:center}
.fvsl-ovl--top{justify-content:center;align-items:flex-start}
.fvsl-ovl--bottom{justify-content:center;align-items:flex-end}
@media (max-width:640px){.fvsl-ovl-card{max-width:84vw;padding:12px}.fvsl-ovl-asset{max-width:60vw}}
`;

let overlayCssInjected = false;
function injectOverlayCss(): void {
  if (overlayCssInjected) return;
  overlayCssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-fullvsl-overlay", "");
  style.textContent = OVERLAY_CSS;
  document.head.appendChild(style);
}

/** Per-overlay runtime card instance. */
interface OverlayInstance {
  overlay: Overlay;
  root: HTMLElement;
  visible: boolean;
  /** One-shot flag: has this overlay been shown this session. */
  shownOnce: boolean;
  /** Auto-dismiss timer (duration / minihook durationMs). */
  dismissTimer: ReturnType<typeof setTimeout> | null;
}

/**
 * Owns overlay reveal lifecycle for one player. The player feeds it progress +
 * lifecycle state and notifies it of pause/complete/unmute moments. Each enabled
 * overlay is rendered into the safe-zone layer and positioned by its slot.
 */
export class OverlayController {
  private readonly layer: HTMLElement;
  private readonly getLayout: () => SafeZoneLayout;
  private readonly emit: (e: PlayerOverlayEvent) => void;
  private readonly actions: OverlayActionHandlers;
  private readonly ctaReveal: { revealMode: "timestamp" | "percent"; revealAt: number; persistAfterReveal: boolean } | null;
  private readonly instances = new Map<string, OverlayInstance>();
  /** Tracks overlays currently suppressed by a user action (e.g. dismissed). */
  private readonly dismissed = new Set<string>();
  private lastProgress: OverlayProgress = { currentTimeSec: 0, maxWatchedSec: 0, durationSec: 0 };
  private lifecycle: OverlayLifecycleState = {
    hasStarted: false,
    isPaused: false,
    completed: false,
    isMuted: false,
  };
  private lastWidthPx = 0;

  constructor(opts: OverlayControllerOptions) {
    injectOverlayCss();
    this.layer = opts.layer;
    this.getLayout = opts.getLayout;
    this.emit = opts.emit;
    this.actions = opts.actions ?? {};
    this.ctaReveal = opts.ctaReveal
      ? {
          revealMode: opts.ctaReveal.revealMode,
          revealAt: opts.ctaReveal.revealAt,
          persistAfterReveal: opts.ctaReveal.persistAfterReveal ?? true,
        }
      : null;
    for (const overlay of opts.overlays) this.buildInstance(overlay);
    this.lastWidthPx = this.readWidth();
    this.relayout();
    // Evaluate initial state (immediate overlays).
    this.evaluate({ ...this.lastProgress }, { ...this.lifecycle });
  }

  /** Build (or rebuild) a single overlay's DOM. */
  private buildInstance(overlay: Overlay): OverlayInstance {
    const existing = this.instances.get(overlay.id);
    if (existing) {
      if (existing.dismissTimer) clearTimeout(existing.dismissTimer);
      existing.root.remove();
    }
    const root = document.createElement("div");
    root.className = "fvsl-ovl";
    root.dataset.overlayId = overlay.id;
    root.dataset.type = overlay.type;
    root.setAttribute("aria-hidden", "true");
    root.setAttribute("role", "dialog");

    const card = document.createElement("div");
    card.className = "fvsl-ovl-card";
    this.applyCardStyle(card, overlay.content);

    // Asset (img/video only; html disabled → text-only fallback).
    this.renderAsset(card, overlay.content);

    // Text fields — textContent only, never innerHTML (security).
    if (overlay.content.text) {
      const t = document.createElement("p");
      t.className = "fvsl-ovl-text";
      t.textContent = overlay.content.text;
      card.appendChild(t);
    }
    if (overlay.content.subtext) {
      const s = document.createElement("p");
      s.className = "fvsl-ovl-sub";
      s.textContent = overlay.content.subtext;
      card.appendChild(s);
    }

    // Action buttons.
    const actionsEl = document.createElement("div");
    actionsEl.className = "fvsl-ovl-actions";
    for (const action of overlay.actions) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "fvsl-ovl-btn";
      btn.dataset.action = action;
      // The primary button uses content.buttonLabel when it matches the first action.
      const label =
        action === this.primaryActionOf(overlay)
          ? overlay.content.buttonLabel || ACTION_LABELS[action]
          : ACTION_LABELS[action];
      btn.textContent = label;
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        this.onAction(overlay, action);
      });
      actionsEl.appendChild(btn);
    }
    if (actionsEl.childElementCount > 0) card.appendChild(actionsEl);

    root.appendChild(card);
    this.layer.appendChild(root);

    const inst: OverlayInstance = {
      overlay,
      root,
      visible: false,
      shownOnce: false,
      dismissTimer: null,
    };
    this.instances.set(overlay.id, inst);
    this.positionInstance(inst);
    return inst;
  }

  /** The action whose button uses content.buttonLabel (the primary action). */
  private primaryActionOf(overlay: Overlay): OverlayAction {
    return overlay.content.buttonAction as OverlayAction | undefined ?? overlay.actions[0] ?? "continue";
  }

  /** Apply overlay content colors/opacity/animation to the card. */
  private applyCardStyle(card: HTMLElement, content: OverlayContent): void {
    const op = clampBgOpacity(content.bgOpacity);
    card.style.background = this.withOpacity(content.colors?.background, op);
    if (content.colors?.text) card.style.color = content.colors.text;
    if (content.font) card.style.fontFamily = content.font;
    const anim = content.animation ?? "fade";
    card.parentElement?.setAttribute("data-anim", anim);
    card.setAttribute("data-anim", anim);
  }

  /** Merge an opacity into a scrim color (hex/rgba → rgba with alpha). */
  private withOpacity(color: string | undefined, opacity: number): string {
    const base = color ?? "rgba(8,12,16,.82)";
    // If the color already carries an alpha channel, honor it; otherwise fold in bgOpacity.
    if (/^rgba?\(/i.test(base) && /,\s*[\d.]+\s*\)/i.test(base)) {
      return base.replace(/,\s*[\d.]+\s*\)$/, `, ${opacity})`);
    }
    return base;
  }

  /**
   * Render the custom asset when present and enabled. `html` assets are disabled
   * (text-only fallback). Broken/mixed-content assets render nothing and rely on
   * the text fields. Image → <img>; video/gif → <video autoplay muted loop>.
   */
  private renderAsset(card: HTMLElement, content: OverlayContent): void {
    const { kind, disabled } = resolveAssetKind(content);
    if (disabled || !kind || !content.assetUrl) return;
    if (!isValidAssetUrl(content.assetUrl)) return;
    if (isMixedContent(content.assetUrl, typeof location !== "undefined" ? location.protocol : "https:")) {
      // Mixed content would be blocked; fall back to text-only silently.
      return;
    }
    if (kind === "image") {
      const img = document.createElement("img");
      img.className = "fvsl-ovl-asset";
      img.alt = "";
      img.loading = "lazy";
      img.addEventListener("error", () => img.remove(), { once: true });
      img.src = content.assetUrl;
      card.appendChild(img);
    } else {
      const vid = document.createElement("video");
      vid.className = "fvsl-ovl-asset";
      vid.muted = true;
      vid.autoplay = true;
      vid.loop = true;
      vid.playsInline = true;
      vid.setAttribute("aria-hidden", "true");
      vid.addEventListener("error", () => vid.remove(), { once: true });
      vid.src = content.assetUrl;
      card.appendChild(vid);
    }
  }

  /** Position an overlay instance inside its safe-zone region. */
  private positionInstance(inst: OverlayInstance): void {
    const layout = this.getLayout();
    const region = regionForPosition(layout, inst.overlay.content.position);
    const posClass = (inst.overlay.content.position ?? "center").replace("_", "");
    inst.root.className = `fvsl-ovl fvsl-ovl--${posClass}`;
    inst.root.setAttribute("data-anim", inst.overlay.content.animation ?? "fade");
    inst.root.style.left = `${(region.x * 100).toFixed(3)}%`;
    inst.root.style.top = `${(region.y * 100).toFixed(3)}%`;
    inst.root.style.width = `${(region.width * 100).toFixed(3)}%`;
    inst.root.style.height = `${(region.height * 100).toFixed(3)}%`;
  }

  /** Re-resolve every overlay's position (call after aspect-ratio change). */
  relayout(): void {
    for (const inst of this.instances.values()) this.positionInstance(inst);
    this.lastWidthPx = this.readWidth();
  }

  private readWidth(): number {
    return this.layer.getBoundingClientRect?.().width ?? 0;
  }

  /** Drive reveal/hide evaluation from a player progress tick. */
  update(progress: OverlayProgress): void {
    this.lastProgress = progress;
    this.maybeRelayoutOnResize();
    this.evaluate(progress, this.lifecycle);
  }

  /** Notify the controller of a lifecycle change (play/pause/ended/unmute). */
  notify(state: Partial<OverlayLifecycleState>): void {
    this.lifecycle = { ...this.lifecycle, ...state };
    this.evaluate(this.lastProgress, this.lifecycle);
  }

  private maybeRelayoutOnResize(): void {
    const w = this.readWidth();
    if (w > 0 && Math.abs(w - this.lastWidthPx) > 1) this.relayout();
  }

  /** Resolve the CTA reveal threshold in seconds against the live duration. */
  private ctaThresholdSec(durationSec: number): number {
    if (!this.ctaReveal) {
      // No CTA config: fall back to the overlay's own timing.at (timestamp).
      const at = this.lastCtaRevealAt();
      return at != null && at >= 0 ? at : Number.POSITIVE_INFINITY;
    }
    if (this.ctaReveal.revealMode === "timestamp") return Math.max(0, this.ctaReveal.revealAt);
    if (!(durationSec > 0) || !Number.isFinite(durationSec)) return Number.POSITIVE_INFINITY;
    const clamped = Math.min(100, Math.max(0, this.ctaReveal.revealAt));
    return (clamped / 100) * durationSec;
  }

  /** The cta_reveal overlay's own timing.at fallback (when no CTA config). */
  private lastCtaRevealAt(): number | undefined {
    for (const inst of this.instances.values()) {
      if (inst.overlay.type === "cta_reveal") return inst.overlay.timing.at;
    }
    return undefined;
  }

  /** Decide show/hide for every overlay based on progress + lifecycle. */
  private evaluate(progress: OverlayProgress, life: OverlayLifecycleState): void {
    const device = deviceFromWidth(this.readWidth() || (typeof window !== "undefined" ? window.innerWidth : 1024));
    for (const inst of this.instances.values()) {
      const { overlay } = inst;
      if (this.dismissed.has(overlay.id)) continue;
      if (!visibilityMatches(overlay.content.visibility, device)) {
        this.hide(inst);
        continue;
      }
      const decision = this.decideShow(inst, progress, life);
      if (decision.show) this.show(inst, progress);
      else if (decision.hide) this.hide(inst);
    }
  }

  /** Show-moment decision for one overlay instance. */
  private decideShow(
    inst: OverlayInstance,
    progress: OverlayProgress,
    life: OverlayLifecycleState,
  ): { show: boolean; hide: boolean } {
    const { overlay } = inst;
    const moment = overlay.timing.show;

    switch (overlay.type) {
      case "start":
        // Show until the first play; dismiss once started.
        return { show: !life.hasStarted, hide: life.hasStarted };
      case "autoplay":
        // Show while muted autoplay is running; dismiss on unmute or first start.
        return { show: life.isMuted, hide: !life.isMuted };
      case "minihook": {
        if (moment !== "timestamp") return { show: false, hide: inst.visible };
        const arrived = timestampArrived(overlay.timing.at, progress.currentTimeSec);
        // One-shot reveal; auto-dismiss handled by the durationMs timer in show().
        return { show: arrived && !inst.shownOnce, hide: false };
      }
      case "cta_reveal": {
        // Mirrors cta.revealAt against the live duration (maxWatched-based).
        const threshold = this.ctaThresholdSec(progress.durationSec);
        const reached = Number.isFinite(threshold) && progress.maxWatchedSec >= threshold;
        const persist = this.ctaReveal?.persistAfterReveal ?? true;
        return { show: reached, hide: !reached && !persist };
      }
      case "pause": {
        // Show when paused past the min-watch threshold; hide on resume.
        if (life.completed) return { show: false, hide: true };
        const met = pauseThresholdMet(progress.maxWatchedSec);
        return { show: life.isPaused && met, hide: !life.isPaused };
      }
      case "end":
        // Show on completion; stays until an action.
        return { show: life.completed, hide: !life.completed };
      default:
        return { show: false, hide: false };
    }
  }

  /** Reveal an overlay instance and (for pause) emit the shown event. */
  private show(inst: OverlayInstance, progress: OverlayProgress): void {
    if (inst.visible) return;
    inst.visible = true;
    inst.shownOnce = true;
    inst.root.classList.add("is-visible");
    inst.root.setAttribute("aria-hidden", "false");
    if (inst.overlay.type === "pause") {
      this.emit(this.buildEvent("pause_overlay_shown", inst.overlay.id, progress));
    }
    // Auto-dismiss for duration / minihook durationMs.
    const ms = inst.overlay.timing.durationMs;
    const auto = inst.overlay.timing.show === "duration" || (inst.overlay.type === "minihook" && ms != null);
    if (auto && ms && ms > 0) {
      if (inst.dismissTimer) clearTimeout(inst.dismissTimer);
      inst.dismissTimer = setTimeout(() => this.hide(inst), ms);
    }
  }

  /** Hide an overlay instance. */
  private hide(inst: OverlayInstance): void {
    if (!inst.visible) return;
    inst.visible = false;
    inst.root.classList.remove("is-visible");
    inst.root.setAttribute("aria-hidden", "true");
    if (inst.dismissTimer) {
      clearTimeout(inst.dismissTimer);
      inst.dismissTimer = null;
    }
  }

  /** Resolve a button action to its side-effect. */
  private onAction(overlay: Overlay, action: OverlayAction): void {
    this.emit(this.buildEvent("pause_overlay_action", overlay.id, this.lastProgress, action));
    switch (action) {
      case "continue":
        this.actions.continue?.();
        // Dismiss start/autoplay/pause overlays on continue.
        this.dismissed.add(overlay.id);
        this.hide(this.instances.get(overlay.id)!);
        break;
      case "restart":
        this.dismissed.delete(overlay.id);
        this.actions.restart?.();
        break;
      case "jump_to_offer":
        this.actions.jumpToOffer?.();
        break;
      case "open_cta":
        this.actions.openCta?.();
        break;
      case "close":
        this.dismissed.add(overlay.id);
        this.hide(this.instances.get(overlay.id)!);
        break;
    }
  }

  private buildEvent(
    name: "pause_overlay_shown" | "pause_overlay_action",
    overlayId: string,
    progress: OverlayProgress,
    action?: OverlayAction,
  ): PlayerOverlayEvent {
    return {
      seq: 0,
      name,
      clientTs: new Date().toISOString(),
      vslId: "",
      sessionId: "",
      visitorId: "",
      overlayId,
      action,
      positionSec: progress.currentTimeSec,
    };
  }

  /** Whether a given overlay instance is currently visible. */
  isVisible(overlayId: string): boolean {
    return this.instances.get(overlayId)?.visible ?? false;
  }

  /** Replace the overlay set (editor live-preview path). */
  setOverlays(overlays: Overlay[]): void {
    const next = new Set(overlays.map((o) => o.id));
    for (const [id, inst] of this.instances) {
      if (!next.has(id)) {
        if (inst.dismissTimer) clearTimeout(inst.dismissTimer);
        inst.root.remove();
        this.instances.delete(id);
        this.dismissed.delete(id);
      }
    }
    for (const overlay of overlays) {
      const existing = this.instances.get(overlay.id);
      if (!existing) {
        this.buildInstance(overlay);
      }
    }
    this.relayout();
    this.evaluate(this.lastProgress, this.lifecycle);
  }

  destroy(): void {
    for (const inst of this.instances.values()) {
      if (inst.dismissTimer) clearTimeout(inst.dismissTimer);
      inst.root.remove();
    }
    this.instances.clear();
    this.dismissed.clear();
  }
}
